import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad, useTriggerLoad } from "@redotech/react-util/load";
import CalendarIcon from "@redotech/redo-icons/calendar-check-01_filled.svg";
import CheckVerifiedIcon from "@redotech/redo-icons/check-verified-01.svg";
import XCircleIcon from "@redotech/redo-icons/x-circle.svg";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import RedoLogoSvg from "@redotech/redo-web/logo.svg";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useParams } from "react-router-dom";
import { downloadCalendarInvite } from "../utils/calendar-invite";

export const CampaignLogin = memo(function CampaignLogin() {
  const { campaignSlug } = useParams();
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );

  const campaignResult = useLoad(async () => {
    if (!campaignSlug) {
      throw new Error("Campaign slug is required");
    }
    return await marketplaceRpcClient.getCampaignBySlug({ slug: campaignSlug });
  }, [campaignSlug, marketplaceRpcClient]);

  const campaign = campaignResult.value?.campaign;
  const error = campaignResult.error?.message;

  const [sendMagicLinkResult, triggerSendMagicLink] = useTriggerLoad(
    async () => {
      if (!campaignSlug) {
        throw new Error("Campaign slug is required");
      }
      return await marketplaceRpcClient.sendMagicLink({ email, campaignSlug });
    },
  );

  // Handle campaign loading state
  if (campaignResult.pending) {
    return (
      <Flex
        align="center"
        className="min-h-screen bg-gray-50"
        dir="column"
        justify="center"
        px="xl"
        py="6xl"
      >
        <Flex className="sm:mx-auto sm:w-full sm:max-w-md" w="full">
          <Flex
            align="center"
            className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200"
            dir="column"
            gap="lg"
            w="full"
          >
            <RedoSpinner size="lg" />
          </Flex>
        </Flex>
      </Flex>
    );
  }

  // Handle campaign loading errors
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-red-200">
            <div className="text-center space-y-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                Campaign not found
              </h1>
              <p className="text-sm text-gray-600">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle case where campaign data is null (not found)
  if (!campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-red-200">
            <div className="text-center space-y-4">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <XCircleIcon className="h-6 w-6 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">
                Campaign not found
              </h1>
              <p className="text-sm text-gray-600">
                The campaign you're looking for doesn't exist or is no longer
                available.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if campaign is active and within time window
  const now = new Date();
  const startTime = new Date(campaign.startTime);
  const endTime = new Date(campaign.endTime);

  if (!campaign.active) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
            <div className="text-center space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">
                Campaign inactive
              </h1>
              <p className="text-sm text-gray-600">
                This campaign is currently inactive.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (now < startTime) {
    const handleAddToCalendar = async () => {
      try {
        await downloadCalendarInvite({ campaign });
      } catch (error) {
        console.error("Failed to download calendar invite:", error);
      }
    };

    const customStyle = campaign.branding?.customCss
      ? {
          backgroundColor: campaign.branding.primaryColor || "#0066cc",
          backgroundImage: campaign.branding.backgroundImage
            ? `url(${campaign.branding.backgroundImage})`
            : undefined,
        }
      : {};

    return (
      <div
        className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8"
        style={customStyle}
      >
        <div className="sm:mx-auto sm:w-full sm:max-w-lg">
          {/* Logo Section */}
          <Flex align="center" justify="center" mb="2xl">
            {campaign.branding?.logoUrl ? (
              <img
                alt={campaign.name}
                className="h-12 w-auto"
                src={campaign.branding.logoUrl}
              />
            ) : (
              <RedoLogoSvg style={{ height: "48px", width: "auto" }} />
            )}
          </Flex>

          <div className="bg-white py-8 px-6 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
            <div className="text-center space-y-6">
              <div className="space-y-2">
                <h1 className="text-2xl font-bold text-gray-900">
                  {campaign.name}
                </h1>
              </div>

              <div className="space-y-4">
                {campaign.description && (
                  <Text fontSize="sm" textColor="secondary">
                    {campaign.description}
                  </Text>
                )}

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <Text fontSize="sm" fontWeight="medium" mb="md">
                    Event Details
                  </Text>
                  <Text fontSize="sm">
                    Starts: {startTime.toLocaleDateString()} at{" "}
                    {startTime.toLocaleTimeString()}
                  </Text>
                </div>

                <div className="space-y-3">
                  <div className="space-y-2">
                    <RedoButton
                      className="w-full"
                      hierarchy="secondary"
                      IconLeading={CalendarIcon}
                      onClick={handleAddToCalendar}
                      size="sm"
                      text="Add to calendar"
                    />

                    <Text fontSize="xs" mt="xs" textColor="secondary">
                      Come back to this page to access the event once it starts.
                    </Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (now > endTime) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200">
            <div className="text-center space-y-4">
              <h1 className="text-2xl font-bold text-gray-900">
                {campaign.name}
              </h1>
              <p className="text-sm text-gray-600">
                This campaign has ended. It was active until{" "}
                {endTime.toLocaleDateString()} at {endTime.toLocaleTimeString()}
                .
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      return;
    }

    triggerSendMagicLink();
    setIsSubmitted(true);
  };

  if (isSubmitted && sendMagicLinkResult.value?.success) {
    return (
      <Flex
        align="center"
        className="min-h-screen bg-gray-50"
        dir="column"
        justify="center"
        px="xl"
        py="6xl"
      >
        <Flex className="sm:mx-auto sm:w-full sm:max-w-md" w="full">
          <Flex
            className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200"
            dir="column"
            gap="xl"
            w="full"
          >
            {/* Success Icon */}
            <Flex
              align="center"
              className="mx-auto h-12 w-12 rounded-full bg-green-100"
              justify="center"
            >
              <CheckVerifiedIcon className="h-6 w-6 text-green-600" />
            </Flex>

            <Flex align="center" dir="column" gap="lg">
              <Text
                as="h1"
                fontSize="2xl"
                fontWeight="bold"
                textColor="primary"
              >
                Check your email
              </Text>

              <Text fontSize="sm" textAlign="center" textColor="tertiary">
                We've sent a magic link to{" "}
                <Text as="span" fontWeight="bold" textColor="primary">
                  {email}
                </Text>
                . Click the link in the email to access {campaign.name}.
              </Text>

              <Flex
                className="bg-blue-50 border border-blue-200 rounded-md"
                p="sm"
              >
                <Text color="blue-800" fontSize="xs">
                  The link will expire in 1 hour. If you don't see the email,
                  check your spam folder.
                </Text>
              </Flex>

              <div className="w-full border-t border-gray-200 my-4" />

              <RedoButton
                className="w-full"
                hierarchy="secondary"
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail("");
                }}
                text="Use a different email address"
              />
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    );
  }

  const customStyle = campaign.branding?.customCss
    ? {
        backgroundColor: campaign.branding.primaryColor || "#0066cc",
        backgroundImage: campaign.branding.backgroundImage
          ? `url(${campaign.branding.backgroundImage})`
          : undefined,
      }
    : {};

  return (
    <div
      className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8"
      style={customStyle}
    >
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Flex align="center" justify="center" mb="2xl">
          {campaign.branding?.logoUrl ? (
            <img
              alt={campaign.name}
              className="h-12 w-auto"
              src={campaign.branding.logoUrl}
            />
          ) : (
            <RedoLogoSvg style={{ height: "48px", width: "auto" }} />
          )}
        </Flex>

        <Flex
          className="bg-white py-8 px-4 shadow-sm sm:rounded-lg sm:px-10 border border-gray-200"
          dir="column"
          gap="xl"
        >
          <Flex align="center" dir="column">
            <Text fontSize="sm" mt="xs" textAlign="center" textColor="tertiary">
              Enter your email address and we'll send you a magic link to access
              this exclusive product drop.
            </Text>
          </Flex>

          <form onSubmit={handleSubmit}>
            <Flex dir="column" gap="lg">
              <Flex dir="column" gap="xs">
                <Text
                  as="span"
                  fontSize="sm"
                  fontWeight="medium"
                  textColor="secondary"
                >
                  Email address
                </Text>
                <RedoTextInput
                  inputTypeHint="email"
                  placeholder="your@email.com"
                  required
                  setValue={setEmail}
                  value={email}
                />
              </Flex>

              <RedoButton
                className="w-full"
                disabled={!email.trim() || sendMagicLinkResult.pending}
                hierarchy="primary"
                size="lg"
                text="Access Marketplace"
                type="submit"
              />
            </Flex>
          </form>
          <Text fontSize="xs" textAlign="center" textColor="tertiary">
            Limited access until: {endTime.toLocaleString()}
          </Text>

          {sendMagicLinkResult.error && (
            <RedoAlert
              color="error"
              description={
                sendMagicLinkResult.error.message ||
                "Something went wrong. Please try again."
              }
              title="Error sending magic link"
            />
          )}
        </Flex>
      </div>
    </div>
  );
});
